/**
 * Admin V3 — Drivers module — Table component
 *
 * 10 columns: Chauffeur (avatar + name + email) | Téléphone | Véhicule
 * (model + plate) | Note (★) | Courses | Statut | Approbation | Abonnement
 * | Créé le | Actions.
 *
 * Row click → /admin-v3/drivers/:driverId (placeholder for now, Phase 2.3
 * decision #1; future phase replaces the DetailPlaceholder with a real
 * read-only detail view).
 *
 * DEFENSIVE STATUS HANDLING: backend canonical enum is
 * available/busy/offline/suspended, but legacy /admin frontend code checks
 * driver.status === 'online'. If a payload ever carries 'online', we map it
 * to 'available' before lookup so the pill renders correctly.
 *
 * NO mutation surface. NO activate/deactivate/approve/reject (out of scope).
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, Users, AlertCircle, RotateCcw, Star } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  EmptyState,
  SmallPill,
  driverStatusMeta,
  driverApprovalMeta,
  driverSubscriptionMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatDate = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

const initialsOf = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(10).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const DriversListTable = ({ drivers, loading, error, isFilterActive, onRetry, locale = 'fr' }) => {
  const navigate = useNavigate();

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-drivers-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('drivers.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-drivers-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('drivers.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={Users}
      title={t('drivers.empty.title', locale)}
      desc={t(isFilterActive ? 'drivers.empty.descFilter' : 'drivers.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {[
                t('drivers.col.driver', locale),
                t('drivers.col.phone', locale),
                t('drivers.col.vehicle', locale),
                t('drivers.col.rating', locale),
                t('drivers.col.rides', locale),
                t('drivers.col.status', locale),
                t('drivers.col.approval', locale),
                t('drivers.col.subscription', locale),
                t('drivers.col.created', locale),
                t('drivers.col.actions', locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : drivers.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              drivers.map((driver) => {
                // Defensive status mapping: tolerate legacy 'online' value.
                const operationalStatus = driver.status === 'online' ? 'available' : driver.status;
                const statusMeta = driverStatusMeta[operationalStatus] || null;
                const approvalMeta = driverApprovalMeta[driver.approval_status] || null;
                const subscriptionMeta = driver.subscription_active
                  ? driverSubscriptionMeta.active
                  : driverSubscriptionMeta.inactive;
                const detailPath = `/admin/drivers/${driver.id}`;

                return (
                  <tr key={driver.id} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                    {/* Chauffeur (avatar + name + email) */}
                    <td className="px-4 py-3.5">
                      <Link
                        to={detailPath}
                        className="flex items-center gap-2.5 group"
                        data-testid={`adminv3-driver-row-${driver.id}`}
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-amber-400 flex-shrink-0">
                          {initialsOf(driver.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 group-hover:text-emerald-700 truncate">
                            {driver.name || '—'}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate">{driver.email || ''}</div>
                        </div>
                      </Link>
                    </td>

                    {/* Téléphone */}
                    <td className="px-4 py-3.5 text-slate-700 font-mono text-[12px]">
                      {driver.phone || '—'}
                    </td>

                    {/* Véhicule */}
                    <td className="px-4 py-3.5">
                      <div className="text-slate-700">{driver.vehicle_model || '—'}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{driver.license_plate || ''}</div>
                    </td>

                    {/* Note */}
                    <td className="px-4 py-3.5">
                      {driver.rating != null ? (
                        <div className="inline-flex items-center gap-1 text-slate-700">
                          <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                          <span className="font-medium">{Number(driver.rating).toFixed(1)}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">{t('drivers.rating.none', locale)}</span>
                      )}
                    </td>

                    {/* Courses */}
                    <td className="px-4 py-3.5 text-slate-700 font-medium">
                      {driver.total_rides ?? 0}
                    </td>

                    {/* Statut (operational) */}
                    <td className="px-4 py-3.5">
                      <SmallPill meta={statusMeta} />
                    </td>

                    {/* Approbation */}
                    <td className="px-4 py-3.5">
                      <SmallPill meta={approvalMeta} />
                    </td>

                    {/* Abonnement */}
                    <td className="px-4 py-3.5">
                      <SmallPill meta={subscriptionMeta} />
                    </td>

                    {/* Créé le */}
                    <td className="px-4 py-3.5 text-slate-500 text-[12px]">
                      {formatDate(driver.created_at, locale)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3.5">
                      <button
                        type="button"
                        onClick={() => navigate(detailPath)}
                        className={cn(
                          'rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors'
                        )}
                        title={t('drivers.action.open', locale)}
                        data-testid={`adminv3-driver-open-${driver.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DriversListTable;
